import type { JSX, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type {
  BootstrapResponse,
  CompanyAuthSession,
} from '@crv/contracts/auth';
import { toCompanyAuthSession } from '@crv/contracts/auth';
import type { CompanyMembershipSummaryDto } from '@crv/contracts/tenancy';
import { clearTokens, getTokens, setTokens, type AuthTokens } from '@crv/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { authClient } from '../api/auth-client';
import { AuthClientError } from '../api/auth-http-client';
import {
  clearWebRefreshManagerTokens,
  registerWebSessionInvalidatedHandler,
  setWebRefreshManagerTokens,
  syncWebRefreshManagerTokensFromStorage,
  webRefreshTokenManager,
} from '../api/auth-refresh-manager';
import { useTenancyStore } from '../tenancy/tenancy-store';
import { recordWebAuthMetric, startWebAuthTimer } from '../observability/auth-bootstrap-metrics';

interface AuthContextValue {
  currentUser: CompanyAuthSession | null;
  bootstrap?: BootstrapResponse | null;
  memberships?: CompanyMembershipSummaryDto[];
  platformPermissions?: string[];
  canAccessPlatform?: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  login(email: string, password: string): Promise<void>;
  selectCompany?(companyId: string): Promise<void>;
  selectIdentity?(): Promise<void>;
  logout(): void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [isBootstrapReady, setIsBootstrapReady] = useState(false);
  const setSelectedCompanyId = useTenancyStore((state) => state.setSelectedCompanyId);
  const clearSelectedCompany = useTenancyStore((state) => state.clearSelectedCompany);
  const autoSelectingRef = useRef<string | null>(null);
  const menuReadyRecordedRef = useRef(false);

  const resetSession = useCallback(() => {
    clearTokens();
    clearWebRefreshManagerTokens();
    clearSelectedCompany();
    autoSelectingRef.current = null;
    menuReadyRecordedRef.current = false;
    queryClient.clear();
    if (!isPublicCapturePath(location.pathname)) {
      navigate('/login', { replace: true });
    }
  }, [clearSelectedCompany, location.pathname, navigate, queryClient]);

  const clearWorkspaceQueries = useCallback(() => {
    queryClient.removeQueries({
      predicate: (query) => {
        const [rootKey, secondKey] = query.queryKey;
        return !(rootKey === 'auth' && secondKey === 'bootstrap');
      },
    });
  }, [queryClient]);

  const handleAuthError = useCallback(
    (error: unknown) => {
      if (error instanceof AuthClientError && error.status === 401) {
        resetSession();
      }
    },
    [resetSession],
  );

  const fetchBootstrap = useCallback(async (): Promise<BootstrapResponse | null> => {
    const tokens = getTokens();
    if (!tokens?.accessToken) {
      return null;
    }
    const endMetric = startWebAuthTimer('bootstrap', { path: '/v1/auth/bootstrap' });

    try {
      const bootstrap = await webRefreshTokenManager.withAutoRefresh(async () => {
        const latestTokens = getTokens();
        if (!latestTokens?.accessToken) {
          throw new Error('No access token available for bootstrap');
        }
        return authClient.bootstrap(latestTokens.accessToken);
      });

      if (bootstrap.mode === 'company') {
        setSelectedCompanyId(bootstrap.activeCompany.company.id);
      }
      endMetric({ success: true });

      return bootstrap;
    } catch (error) {
      endMetric({ success: false, note: error instanceof Error ? error.message : 'bootstrap-error' });
      handleAuthError(error);
      throw error;
    }
  }, [handleAuthError, setSelectedCompanyId]);

  const hasAccessToken = Boolean(getTokens()?.accessToken);
  const bootstrapQuery = useQuery({
    queryKey: ['auth', 'bootstrap'],
    queryFn: fetchBootstrap,
    enabled: hasAccessToken && isBootstrapReady,
    staleTime: 60_000,
    retry: false,
  });

  const bootstrap = bootstrapQuery.data ?? null;
  const memberships = bootstrap?.memberships ?? [];
  const platformPermissions = useMemo(
    () =>
      bootstrap?.mode === 'identity'
        ? bootstrap.platformPermissions
        : (bootstrap?.platformPermissions ?? []),
    [bootstrap],
  );
  const session = useMemo<CompanyAuthSession | null>(
    () =>
      bootstrap?.mode === 'company'
        ? toCompanyAuthSession(bootstrap)
        : null,
    [bootstrap],
  );

  const isLoading = hasAccessToken && (!isBootstrapReady || bootstrapQuery.isLoading);
  const isAuthenticated = hasAccessToken && isBootstrapReady && !bootstrapQuery.isLoading && !bootstrapQuery.isError;
  const canAccessPlatform = platformPermissions.length > 0;

  const selectCompany = useCallback(
    async (companyId: string) => {
      const normalized = companyId.trim();
      if (!normalized) {
        return;
      }
      const endMetric = startWebAuthTimer('select-company', { path: '/v1/auth/select-company' });

      try {
        await webRefreshTokenManager.withAutoRefresh(async () => {
          const latestTokens = getTokens();
          if (!latestTokens?.accessToken) {
            throw new Error('No access token available for company selection');
          }

          const response = await authClient.selectCompany(latestTokens.accessToken, {
            companyId: normalized,
          });

          const nextTokens: AuthTokens = {
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
          };
          setTokens(nextTokens);
          setWebRefreshManagerTokens(nextTokens);
          setSelectedCompanyId(normalized);
        });

        clearWorkspaceQueries();
        await bootstrapQuery.refetch();
        endMetric({ success: true });
      } catch (error) {
        endMetric({ success: false, note: error instanceof Error ? error.message : 'select-company-error' });
        throw error;
      }
    },
    [bootstrapQuery, clearWorkspaceQueries, setSelectedCompanyId],
  );

  const selectIdentity = useCallback(async () => {
    const endMetric = startWebAuthTimer('select-identity', { path: '/v1/auth/select-identity' });

    try {
      await webRefreshTokenManager.withAutoRefresh(async () => {
        const latestTokens = getTokens();
        if (!latestTokens?.accessToken) {
          throw new Error('No access token available for platform selection');
        }

        const response = await authClient.selectIdentity(latestTokens.accessToken, {});
        const nextTokens: AuthTokens = {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        };
        setTokens(nextTokens);
        setWebRefreshManagerTokens(nextTokens);
      });

      clearWorkspaceQueries();
      await bootstrapQuery.refetch();
      endMetric({ success: true });
    } catch (error) {
      endMetric({ success: false, note: error instanceof Error ? error.message : 'select-identity-error' });
      throw error;
    }
  }, [bootstrapQuery, clearWorkspaceQueries]);

  useEffect(() => {
    syncWebRefreshManagerTokensFromStorage();
    setIsBootstrapReady(true);
  }, []);

  useEffect(() => {
    registerWebSessionInvalidatedHandler(() => {
      resetSession();
    });

    return (): void => {
      registerWebSessionInvalidatedHandler(null);
    };
  }, [resetSession]);

  useEffect(() => {
    if (!bootstrap || bootstrap.mode !== 'company' || menuReadyRecordedRef.current) {
      return;
    }

    menuReadyRecordedRef.current = true;
    recordWebAuthMetric({
      kind: 'menu-ready',
      at: new Date().toISOString(),
      success: true,
      note: bootstrap.activeCompany.company.id,
    });
  }, [bootstrap]);

  useEffect(() => {
    if (!bootstrap || bootstrap.mode !== 'identity') {
      autoSelectingRef.current = null;
      return;
    }

    if (location.pathname.startsWith('/platform')) {
      autoSelectingRef.current = null;
      return;
    }

    if (bootstrap.nextAction !== 'auto-select-company' || !bootstrap.autoSelectCompanyId) {
      autoSelectingRef.current = null;
      return;
    }

    if (autoSelectingRef.current === bootstrap.autoSelectCompanyId) {
      return;
    }

    autoSelectingRef.current = bootstrap.autoSelectCompanyId;
    void selectCompany(bootstrap.autoSelectCompanyId).catch(() => {
      autoSelectingRef.current = null;
    });
  }, [bootstrap, location.pathname, selectCompany]);

  const login = useCallback(
    async (email: string, password: string) => {
      const endMetric = startWebAuthTimer('login', { path: '/v1/auth/login' });
      try {
        const response = await authClient.login({ email, password });
        const tokens: AuthTokens = {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        };
        setTokens(tokens);
        setWebRefreshManagerTokens(tokens);
        clearSelectedCompany();
        autoSelectingRef.current = null;
        menuReadyRecordedRef.current = false;
        await bootstrapQuery.refetch();
        endMetric({ success: true });
      } catch (error) {
        endMetric({ success: false, note: error instanceof Error ? error.message : 'login-error' });
        throw error;
      }
    },
    [bootstrapQuery, clearSelectedCompany],
  );

  const logout = useCallback(() => {
    resetSession();
  }, [resetSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      currentUser: session,
      bootstrap,
      memberships,
      platformPermissions,
      canAccessPlatform,
      isAuthenticated,
      isLoading,
      login,
      selectCompany,
      selectIdentity,
      logout,
    }),
    [
      bootstrap,
      canAccessPlatform,
      isAuthenticated,
      isLoading,
      login,
      memberships,
      logout,
      platformPermissions,
      selectCompany,
      selectIdentity,
      session,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return ctx;
}

function isPublicCapturePath(pathname: string): boolean {
  return pathname.startsWith('/capture/shipments/weigh/');
}
